import { GraphQLResult } from '@aws-amplify/api-graphql';
import { CreateJobCodeInput, CreateJobCodeMutation, GetJobCodeQuery, JobCode } from 'API';
import {
  SEARCH_SORT_DIRECTION,
  TRANSACTION_SORT_FIELD,
  SearchSortDirection,
  LocalTransactionSortField,
} from 'app/util/searchTypes';
import { paidolService, GetJobCodesInput } from 'app/services/paidol.service';
import { SpxTransactionsPage, transactionService } from 'app/services/transaction.service';
import type { SearchSpxTransactionsInput } from 'CDK_API';
import { graphqlOperation } from 'aws-amplify';
import { addMonths, format } from 'date-fns';
import { getJobCode as getJobCodeQuery } from 'graphql/queries';
// SEE-1232 row h: the job-code actual-spend read goes to CDK (cardService).
import { cardService } from 'app/services/card.service';
import moment from 'moment';
import { isNotNullOrUndefined } from 'util/typeGuards';
import { getCardById } from './cardService';
import { graphqlRequest } from './middleware';
import { Dispatch } from '@reduxjs/toolkit';

/**
 * SEE-190: served by the CDK `getJobCodes` resolver via paidolService. Kept here so
 * the slice, the Job Codes page and the reimbursement sheet keep their import path.
 * `yearAndMonth` is intentionally not forwarded (spec D10). The resolver honours it
 * when sent, but callers compute it from the browser's local clock, so forwarding
 * would move the month boundary for `spent` from UTC to local time. The Amplify
 * path dropped it for the same reason; the backend resolves the current UTC month.
 */
export const getJobCodes = async ({ paidolId, nextToken }: GetJobCodesInput) => {
  const result = await paidolService.getJobCodes({ paidolId, nextToken });
  return result ?? undefined;
};

const createJobCodeMutation = /* GraphQL */ `
  mutation CreateJobCode($input: CreateJobCodeInput!) {
    createJobCode(input: $input) {
      id
      paidolId
      authPaidolId
      description
      code
      isActive
      budget
      jobCodeImportId
    }
  }
`;

export const createJobCode = async (
  { id, paidolId, authPaidolId, description, code, isActive, budget, jobCodeImportId }: CreateJobCodeInput,
  dispatch: Dispatch
) => {
  const input: CreateJobCodeInput = {
    id,
    paidolId,
    authPaidolId,
    description,
    code,
    isActive,
    budget,
    jobCodeImportId,
  };
  return (
    graphqlRequest(graphqlOperation(createJobCodeMutation, { input }), dispatch) as Promise<
      GraphQLResult<CreateJobCodeMutation>
    >
  ).then((result) => {
    return result.data?.createJobCode as JobCode;
  });
};

export const getJobCodesActualSpend = async (
  paidolId: string,
  jobCodeId: string,
  yearAndMonth: string = moment().utc().format('YYYY-MM'),
  // Kept so JobCodeSheet's call is unchanged; the CDK read needs no dispatch.
  _dispatch?: Dispatch
) => {
  // Transport flip. Amplify's `yearAndMonthAmount: { beginsWith }` + `filter: { jobCodeId }`
  // becomes CDK's point read on `{ paidolId, yearAndMonth, jobCodeId }` (0 or 1 items), so
  // `.at(0)` resolves the same record as before.
  const items = await cardService.listBudgetSpendByMonthAndJobCodeIdByAmount<{
    amount?: number | null;
  }>({ paidolId, yearAndMonth, jobCodeId });
  return items.at(0);
};

export const getJobCode = async ({ jobCodeId }: any, dispatch: Dispatch) => {
  const variables = { id: jobCodeId };
  return (
    graphqlRequest(graphqlOperation(getJobCodeQuery, variables), dispatch) as Promise<
      GraphQLResult<GetJobCodeQuery>
    >
  ).then((result) => {
    return result.data?.getJobCode as JobCode | undefined;
  });
};

export interface GetJobCodeTransactionsArgs {
  // SEE-1393-adjacent: `SpxTransactionsFilterInput.paidolId` is `EqualCondition!`.
  // Omitting it is what made every call fail input coercion before it ran.
  paidolId: string;
  jobCodeId: string;
  nextToken?: string;
  filterDate?: Date | null;
  searchTerms?: string | null;
  sortField?: LocalTransactionSortField;
  sortOrder?: SearchSortDirection;
}

/**
 * SEE-301: repointed to the CDK API, and fixed. This was written against
 * `searchHighnoteTransactionEvents`' variables but pointed at the
 * `searchSpxTransactions` document, so every field disagreed with the schema and
 * the job-code transaction list was permanently empty. Four separate faults:
 *
 *  1. no `paidolId` — `SpxTransactionsFilterInput.paidolId` is `EqualCondition!`,
 *     so the request failed coercion before reaching a resolver:
 *     "Variable 'input' has coerced Null value for NonNull type 'EqualCondition!'".
 *  2. `sort` was a list — `SearchSpxTransactionsInput.sort` is a single
 *     `SortFilterInput`.
 *  3. the search term used a top-level `or` of `merchantName`/`merchantCategory`,
 *     but this filter's `or` only accepts `paymentCardId`. Those two fields live on
 *     `SpxTransactionsOptionalFilterInput`, reachable only via `and -> or`, and
 *     `EqualCondition` has no `match` — only `wildcard`.
 *  4. it read `data.searchHighnoteTransactionEvents`, a key the document never
 *     returns.
 *
 * Fixing this changes behaviour: a list that always rendered empty will now show
 * rows.
 */
export const getJobCodeTransactions = async ({
  paidolId,
  jobCodeId,
  nextToken,
  filterDate,
  searchTerms,
  sortField = TRANSACTION_SORT_FIELD.eventDate,
  sortOrder = SEARCH_SORT_DIRECTION.desc,
}: GetJobCodeTransactionsArgs) => {
  const payload = await transactionService.searchSpxTransactions({
    filter: {
      paidolId: { eq: paidolId },
      // `jobCodeId` is an ExistsCondition, which does accept `eq` alongside `exists`.
      jobCodeId: { eq: jobCodeId },
      ...(filterDate && {
        eventDate: {
          gt: format(new Date(filterDate), 'yyyy-MM'),
          lt: format(addMonths(new Date(filterDate), 1), 'yyyy-MM'),
        },
      }),
      ...(searchTerms && {
        and: [
          {
            or: [
              { merchantName: { wildcard: `*${searchTerms}*` } },
              { merchantCategory: { wildcard: `*${searchTerms}*` } },
            ],
          },
        ],
      }),
    },
    sort: { field: sortField, direction: sortOrder },
    nextToken,
    limit: 20,
  } as SearchSpxTransactionsInput);

  return payload as SpxTransactionsPage | null;
};

interface loadJobCodeCardsPayload {
  paymentCardId: string;
  paidolId: string;
}

export const getJobCodeCards = async (
  payload: loadJobCodeCardsPayload[],
  yearAndMonth: string = format(new Date(), 'yyyy-MM'),
  dispatch: Dispatch
) => {
  const promises = payload
    .filter((p) => p.paidolId && p.paymentCardId)
    .map((p) =>
      getCardById({
        filter: {
          paidolId: { eq: p.paidolId },
          and: [{ or: [{ paymentCardId: { eq: p.paymentCardId } }] }],
        },
      })
    );
  const results = await Promise.all(promises);
  return results.filter(isNotNullOrUndefined);
};
